/** TanStack Query hooks for lessons: detail with steps, version history. */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { Lesson, Step } from '@formency/shared';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Raw API response shape — flat steps array with category per step. */
interface LessonWithStepsRaw extends Lesson {
  steps: (Step & { category: 'learn' | 'revise' })[];
  isPublishedCourse?: boolean;
}

/** Lesson with steps split by category. */
export interface LessonWithSteps extends Lesson {
  learnSteps: Step[];
  reviseSteps: Step[];
  isPublishedCourse: boolean;
}

/** Single entry in the lesson version history list. */
export interface VersionHistoryEntry {
  version: number;
  notes: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  publishedByName: string | null;
}

/** Paginated version history response. */
export interface VersionHistoryResponse {
  data: VersionHistoryEntry[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// ─── Query key factory ─────────────────────────────────────────────────────

export const lessonKeys = {
  all: ['f', 'lessons'] as const,
  detail: (id: string) => [...lessonKeys.all, id] as const,
  withSteps: (id: string) => [...lessonKeys.all, id, 'with-steps'] as const,
  versions: (id: string) => [...lessonKeys.all, id, 'versions'] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────────

/** Fetch a lesson with all steps, optionally scoped to a course version (draft). */
export function useLessonWithSteps(lessonId: string, courseVersionId?: string) {
  return useQuery<LessonWithSteps>({
    queryKey: [...lessonKeys.withSteps(lessonId), { courseVersionId }],
    queryFn: async () => {
      const params: Record<string, string> = { include: 'steps' };
      if (courseVersionId) params.courseVersionId = courseVersionId;
      const raw = await api.get<LessonWithStepsRaw>(`/lessons/${lessonId}`, params);
      const { steps, isPublishedCourse, ...rest } = raw;
      return {
        ...rest,
        learnSteps: steps.filter((s) => s.category === 'learn'),
        reviseSteps: steps.filter((s) => s.category === 'revise'),
        isPublishedCourse: isPublishedCourse ?? false,
      };
    },
    enabled: !!lessonId,
    staleTime: 5 * 60_000,
  });
}

/** Fetch paginated version history for a lesson. */
export function useLessonVersions(lessonId: string, page = 1, pageSize = 20) {
  return useQuery<VersionHistoryResponse>({
    queryKey: [...lessonKeys.versions(lessonId), { page, pageSize }],
    queryFn: () =>
      api.get<VersionHistoryResponse>(`/lessons/${lessonId}/versions`, { page, pageSize }),
    enabled: !!lessonId,
  });
}
